/**
 * Source unique de tokens de styles pour les exports Word et PDF.
 *
 * Objectif : garantir qu'un même document rendu en .docx (via `exportWord.ts`)
 * et en .pdf (via `exportPdf.ts`) soit strictement identique — mêmes couleurs
 * de bordures, mêmes fonds alternés, mêmes ratios d'éclaircissement du fond
 * d'en-tête, mêmes proportions de marges/paddings/indentations.
 *
 * Convention :
 *  - Les couleurs sont exprimées en hex 6 caractères SANS `#` (format docx).
 *    Les helpers `hexToRgbToken` / `lightenHexToken` produisent les mêmes
 *    valeurs pour jsPDF (RGB) ou docx (hex).
 *  - Les proportions (padding, indentation) sont exprimées en ratios sur
 *    `bodySize`. Chaque exporter les convertit dans son unité native
 *    (DXA/twips pour Word, mm pour jsPDF) via un helper local.
 */

// ---------- Palette partagée ------------------------------------------------

/** Couleur des bordures de cellules (Slate 300). */
export const TABLE_BORDER_HEX = "CBD5E1";
/** Fond des lignes paires d'un tableau (Slate 50). */
export const TABLE_ALT_ROW_HEX = "F8FAFC";
/** Fond par défaut d'un en-tête de tableau lorsqu'aucune couleur primaire n'est définie. */
export const TABLE_HEADER_FALLBACK_HEX = "E8E8E8";
/** Fond neutre pour les cellules "muted" (ex : première colonne). */
export const CELL_NEUTRAL_MUTED_HEX = "F2F2F7";
/** Fond des blocs de code. */
export const CODE_BG_HEX = "F4F4F8";
/** Fond par défaut d'un blockquote sans thème. */
export const QUOTE_BG_FALLBACK_HEX = "F5F5F8";

/** Texte principal par défaut (Slate 800). */
export const TEXT_DARK_HEX = "1E293B";
/** Texte "muted" par défaut (Slate 500). */
export const TEXT_MUTED_HEX = "64748B";

// ---------- Ratios d'éclaircissement ---------------------------------------

/** Ratio pour dériver le fond d'en-tête depuis la couleur primaire. */
export const HEADER_FILL_LIGHTEN_RATIO = 0.82;
/** Ratio pour dériver le fond de blockquote depuis la couleur primaire. */
export const QUOTE_BG_LIGHTEN_RATIO = 0.92;
/** Ratio "générique" pour éclaircir une couleur (fallback docx). */
export const DEFAULT_LIGHTEN_RATIO = 0.85;

// ---------- Proportions (bordures, paddings) --------------------------------

/**
 * Épaisseur des bordures de cellules.
 *  - Word : `size: 1` (huitièmes de point, ≈ 0.125 pt)
 *  - PDF  : `lineWidth: 0.15` mm ≈ 0.425 pt
 * Ces deux valeurs produisent visuellement une bordure "hairline" équivalente.
 */
export const TABLE_BORDER_WIDTH_DOCX_EIGHTHS = 1;
export const TABLE_BORDER_WIDTH_PDF_MM = 0.15;

/**
 * Padding de cellule exprimé en ratio de `bodySize` (pt).
 *  - horizontal ≈ 0.33 × bodySize
 *  - vertical   ≈ 0.20 × bodySize
 *
 * Word les convertit en twips (via `Math.round(step / 3)` et `step / 4`
 * où `step = bodySize_halfpoints * 16 ≈ 20 twips/pt × bodySize`).
 * PDF utilise directement des mm après conversion pt → mm.
 */
export const CELL_PAD_H_RATIO = 1 / 3;
export const CELL_PAD_V_RATIO = 1 / 4;

/**
 * Indentation d'un niveau de blockquote / liste, en ratio de `bodySize` (pt).
 * `step` docx = 16 × bodySize_halfpoints ≈ 1.6em × 20 twips/em.
 * En mm : `1.6em × pt(bodySize) × 0.352778` ≈ conversion utilisée par PDF.
 */
export const BLOCK_INDENT_EM = 1.6;

/**
 * Largeur de la bande latérale d'un blockquote.
 * PDF : 1.6 mm.
 * Word : bordure gauche `size: 18` huitièmes de pt = 2.25 pt ≈ 0.8 mm
 *        (le rendu Word inclut un padding interne implicite).
 */
export const QUOTE_BAR_WIDTH_PDF_MM = 1.6;
export const QUOTE_BAR_WIDTH_DOCX_EIGHTHS = 18;
/** Espace entre la bande de citation et le texte (ratio de `bodySize`). */
export const QUOTE_BAR_GAP_RATIO = 0.25;

// ---------- Helpers de conversion ------------------------------------------

export type RGB = { r: number; g: number; b: number };

/** Convertit un hex (avec ou sans `#`) vers un objet RGB. */
export function hexToRgbToken(hex: string): RGB {
  const h = hex.replace("#", "");
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}

/** Éclaircit une couleur hex vers le blanc selon `ratio` (0 = inchangé, 1 = blanc). */
export function lightenHexToken(hex: string, ratio = DEFAULT_LIGHTEN_RATIO): RGB {
  const { r, g, b } = hexToRgbToken(hex);
  return {
    r: Math.round(r + (255 - r) * ratio),
    g: Math.round(g + (255 - g) * ratio),
    b: Math.round(b + (255 - b) * ratio),
  };
}

/** Renvoie un hex sans `#` en majuscules ou `undefined` si invalide. */
export function normalizeHexNoHash(hex?: string | null): string | undefined {
  if (!hex) return undefined;
  const h = hex.replace("#", "").trim();
  return /^[0-9A-Fa-f]{6}$/.test(h) ? h.toUpperCase() : undefined;
}

/** Version hex (sans `#`) d'un éclaircissement — utile pour docx. */
export function lightenHexNoHashToken(hex: string, ratio = DEFAULT_LIGHTEN_RATIO): string {
  const rgb = lightenHexToken(hex, ratio);
  const toHex = (n: number) => n.toString(16).padStart(2, "0").toUpperCase();
  return `${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
}

// ---------- Polices par défaut ---------------------------------------------

/** Police sans-serif par défaut (utilisée pour le corps et les titres si le profil n'en fournit pas). */
export const DEFAULT_SANS_FONT = "Calibri";
/** Police monospace par défaut (code inline & fenced blocks). */
export const DEFAULT_MONO_FONT = "Consolas";
/** Famille de police PDF (jsPDF ne connaît que "helvetica", "courier", "times"). */
export const PDF_SANS_FAMILY = "helvetica";
export const PDF_MONO_FAMILY = "courier";

// ---------- Tailles typographiques (en points) -----------------------------

/** Taille par défaut du corps (pt). */
export const DEFAULT_BODY_PT = 11;
/** Taille par défaut d'un H1 (pt). */
export const DEFAULT_H1_PT = 13;
/** Taille par défaut d'un H2 (pt). */
export const DEFAULT_H2_PT = 11.5;

/** Bornes acceptées pour les tailles lues depuis un `StyleProfileExport`. */
export const BODY_PT_MIN = 6;
export const BODY_PT_MAX = 16;
export const H1_PT_MIN = 8;
export const H1_PT_MAX = 36;
export const H2_PT_MIN = 8;
export const H2_PT_MAX = 28;

/** Ratios PDF appliqués aux tailles du profil (jsPDF utilise des points, mais
 *  historiquement le rendu PDF est plus compact que Word — on garde ces
 *  facteurs pour rester visuellement proches). */
export const PDF_H1_SIZE_RATIO = 0.55;
export const PDF_H2_SIZE_RATIO = 0.6;
export const PDF_BODY_SIZE_RATIO = 0.95;

/** Bornes de sécurité côté PDF (pt). */
export const PDF_H1_MIN = 11;
export const PDF_H1_MAX = 20;
export const PDF_H2_MIN = 10;
export const PDF_H2_MAX = 16;
export const PDF_BODY_MIN = 8.5;
export const PDF_BODY_MAX = 13;

/** Tailles PDF par défaut (fallback quand aucun profil). */
export const PDF_DEFAULT_H1 = 13;
export const PDF_DEFAULT_H2 = 11;
export const PDF_DEFAULT_BODY = 10.5;

// ---------- Interlignes ----------------------------------------------------

/** Interligne par défaut du corps.
 *  - Word : unités 240èmes (240 = simple, 288 ≈ 1.2, 360 = 1.5).
 *  - PDF  : multiplicateur × `PDF_LINE_HEIGHT_RATIO × fontSize`.
 */
export const DEFAULT_LINE_HEIGHT_FACTOR = 1.2;
/** Correspondance Word (240ths of a line) pour l'interligne par défaut. */
export const DEFAULT_LINE_HEIGHT_WORD_240 = Math.round(240 * DEFAULT_LINE_HEIGHT_FACTOR);
/** Ratio hauteur de ligne PDF (pt → mm effectifs) : `fontSize * ratio * lh`. */
export const PDF_LINE_HEIGHT_RATIO = 0.5;

/** Bornes acceptées pour un interligne personnalisé (via `[[BLK lh=…]]`). */
export const LINE_HEIGHT_MIN = 1;
export const LINE_HEIGHT_MAX = 3;

// ---------- Espacements paragraphes ----------------------------------------

/** Multiplicateurs `spacing.before` par rapport à la taille du heading. */
export const HEADING_SPACING_BEFORE_RATIO = 3;
/** Multiplicateurs `spacing.after` par rapport à la taille du heading. */
export const HEADING_SPACING_AFTER_RATIO = 1.5;

// ---------- Marges de page PDF (mm) ----------------------------------------

/** Marge extérieure d'une page PDF (mm) — ≈ 2 cm. */
export const PDF_PAGE_MARGIN_MM = 20;

// ---------- Tables PDF -----------------------------------------------------

/** Padding horizontal d'une cellule PDF (mm). */
export const TABLE_CELL_PAD_X_MM = 2.2;
/** Padding vertical d'une cellule PDF (mm). */
export const TABLE_CELL_PAD_Y_MM = 1.8;
/** Taille de police des cellules de table (pt). */
export const TABLE_FONT_SIZE_PT = 9.5;

// ---------- Blocs de code PDF ---------------------------------------------

/** Padding horizontal d'un bloc de code PDF (mm). */
export const CODE_BLOCK_PAD_X_MM = 2;
/** Padding vertical d'un bloc de code PDF (mm). */
export const CODE_BLOCK_PAD_Y_MM = 1.5;
/** Largeur de la bande latérale accent d'un bloc de code (mm). */
export const CODE_BLOCK_ACCENT_BAR_MM = 0.6;

// ---------- Boutons PDF ---------------------------------------------------

/** Padding horizontal d'un bouton PDF (mm). */
export const BUTTON_PAD_X_MM = 6;
/** Padding vertical d'un bouton PDF (mm). */
export const BUTTON_PAD_Y_MM = 2.2;
/** Rayon d'arrondi d'un bouton PDF (mm). */
export const BUTTON_RADIUS_MM = 1.5;

// ---------- Blockquote PDF -------------------------------------------------

/** Espace entre la bande de citation et le texte (mm). */
export const QUOTE_BAR_GAP_MM = 2.8;
/** Espace additionnel après la bande pour aligner sur `step` Word (mm). */
export const QUOTE_INDENT_EXTRA_MM = 3;

// ---------- Alignements par défaut ----------------------------------------

/** Alignement par défaut d'un H1 lorsque le bloc source n'en spécifie pas. */
export const DEFAULT_H1_ALIGN: "left" | "center" | "right" | "justify" = "center";

// ---------- Helpers de résolution profil → tailles ------------------------

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

/** Résout la taille de corps Word (pt) depuis un profil. */
export function resolveBodyPt(profileBody?: number): number {
  return profileBody && profileBody > BODY_PT_MIN
    ? Math.min(BODY_PT_MAX, profileBody)
    : DEFAULT_BODY_PT;
}
/** Résout la taille H1 Word (pt) depuis un profil. */
export function resolveH1Pt(profileH1?: number): number {
  return profileH1 && profileH1 > H1_PT_MIN
    ? Math.min(H1_PT_MAX, profileH1)
    : DEFAULT_H1_PT;
}
/** Résout la taille H2 Word (pt) depuis un profil. */
export function resolveH2Pt(profileH2?: number): number {
  return profileH2 && profileH2 > H2_PT_MIN
    ? Math.min(H2_PT_MAX, profileH2)
    : DEFAULT_H2_PT;
}

/** Résout la taille PDF H1 (pt) depuis un profil. */
export function resolvePdfH1(profileH1?: number): number {
  return profileH1
    ? clamp(profileH1 * PDF_H1_SIZE_RATIO, PDF_H1_MIN, PDF_H1_MAX)
    : PDF_DEFAULT_H1;
}
/** Résout la taille PDF H2 (pt) depuis un profil. */
export function resolvePdfH2(profileH2?: number): number {
  return profileH2
    ? clamp(profileH2 * PDF_H2_SIZE_RATIO, PDF_H2_MIN, PDF_H2_MAX)
    : PDF_DEFAULT_H2;
}
/** Résout la taille PDF body (pt) depuis un profil. */
export function resolvePdfBody(profileBody?: number): number {
  return profileBody
    ? clamp(profileBody * PDF_BODY_SIZE_RATIO, PDF_BODY_MIN, PDF_BODY_MAX)
    : PDF_DEFAULT_BODY;
}

/** Clamp un multiplicateur d'interligne fourni par l'utilisateur. */
export function clampLineHeight(value: number): number {
  return clamp(value, LINE_HEIGHT_MIN, LINE_HEIGHT_MAX);
}

